/**
 * Notification API Repository
 *
 * home_karte_api との通信とデータ変換を担当
 */

import { ENV } from '../../../shared/config/env';
import { createApiClient } from '../../../shared/services/api';
import type { ApiResult } from '../../../shared/types/api';
import type { Notification, NotificationType } from '../types';
import {
  allNotifications,
  getNotificationById as getMockById,
  markNotificationAsRead as markMockAsRead,
  getUnreadCount as getMockUnreadCount,
} from './notificationData';

// 環境変数からAPIベースURLを取得（未設定時はモック）
const API_BASE_URL = ENV.API_BASE_URL;
const USE_MOCK = !API_BASE_URL;

/**
 * デバッグログをFlutter WebViewに送信
 * REACT_APP_DEBUG環境変数がtrueの場合のみ出力
 */
const sendDebugLog = (message: string): void => {
  if (!ENV.DEBUG) return;

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const webview = (window as any).DebugChannel;
    if (webview?.postMessage) {
      webview.postMessage(message);
    }
    console.log('[Notification]', message);
  } catch {
    // WebView外での実行時は無視
  }
};

/**
 * API レスポンス型
 */
interface NotificationListApiResponse {
  total: number;
  page: number;
  limit: number;
  notifications: NotificationApiItem[];
}

interface NotificationApiItem {
  notificationId: string;
  title: string;
  publicatedAt: string;
  notificationType: string;
  isReaded: boolean;
}

interface NotificationDetailApiResponse {
  notificationId: string;
  title: string;
  body: string;
  publicatedAt: string;
  notificationType: string;
  isReaded: boolean;
}

/**
 * notificationTypeの変換
 * API側の値をフロント用のenumにマッピング
 */
const mapNotificationType = (apiType: string): NotificationType => {
  const typeMap: Record<string, NotificationType> = {
    'メンテナンス': 'maintenance',
    '重要なお知らせ': 'important',
    'お知らせ': 'info',
    'お得な情報': 'deals',
  };
  return typeMap[apiType] || 'info';
};

/**
 * APIレスポンスをフロント用の型に変換（一覧用）
 */
const transformApiItem = (item: NotificationApiItem): Notification => ({
  id: item.notificationId,
  type: mapNotificationType(item.notificationType),
  status: item.isReaded ? 'read' : 'unread',
  title: item.title,
  body: '', // 一覧では空、詳細で取得
  createdAt: item.publicatedAt,
});

/**
 * 詳細APIレスポンスをフロント用の型に変換
 */
const transformApiDetail = (detail: NotificationDetailApiResponse): Notification => ({
  id: detail.notificationId,
  type: mapNotificationType(detail.notificationType),
  status: detail.isReaded ? 'read' : 'unread',
  title: detail.title,
  body: detail.body,
  createdAt: detail.publicatedAt,
});

/**
 * Notification Repository
 */
export const notificationRepository = {
  /**
   * お知らせ一覧を取得
   */
  getAll: async (accountId: string, token?: string): Promise<ApiResult<Notification[]>> => {
    if (USE_MOCK) {
      // モックモード
      sendDebugLog(`getAll: モックモード使用 (API_BASE_URL未設定)`);
      await new Promise(resolve => setTimeout(resolve, 300));
      sendDebugLog(`getAll: モックデータ返却 (${allNotifications.length}件)`);
      return { success: true, data: allNotifications };
    }

    sendDebugLog(`getAll: API呼出 accountId=${accountId}, tokenあり=${!!token}`);

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const client = createApiClient({ baseUrl: API_BASE_URL, headers });
    const result = await client.get<NotificationListApiResponse>(
      `/api/v1/notifications?accountId=${accountId}&page=1&limit=100&unreadOnly=false`
    );

    if (!result.success) {
      sendDebugLog(`getAll: APIエラー ${JSON.stringify(result.error)}`);
      return result;
    }

    sendDebugLog(`getAll: APIレスポンス ${JSON.stringify(result.data)}`);
    const notifications = result.data.notifications.map(transformApiItem);
    return { success: true, data: notifications };
  },

  /**
   * お知らせ詳細を取得
   */
  getById: async (notificationId: string, accountId: string, token?: string): Promise<ApiResult<Notification | null>> => {
    if (USE_MOCK) {
      // モックモード
      sendDebugLog(`getById: モックモード使用 (API_BASE_URL未設定)`);
      await new Promise(resolve => setTimeout(resolve, 200));
      const notification = getMockById(notificationId);
      sendDebugLog(`getById: モックデータ返却 notificationId=${notificationId} found=${!!notification}`);
      return { success: true, data: notification || null };
    }

    sendDebugLog(`getById: API呼出 notificationId=${notificationId}, tokenあり=${!!token}`);

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const client = createApiClient({ baseUrl: API_BASE_URL, headers });
    const result = await client.get<NotificationDetailApiResponse>(
      `/api/v1/notifications/${notificationId}?accountId=${accountId}`
    );

    sendDebugLog(`getById: API結果 success=${result.success}`);

    if (!result.success) {
      sendDebugLog(`getById: APIエラー ${JSON.stringify(result.error)}`);
      return result;
    }

    sendDebugLog(`getById: APIレスポンス ${JSON.stringify(result.data)}`);
    const notification = transformApiDetail(result.data);
    return { success: true, data: notification };
  },

  /**
   * お知らせを既読にする
   */
  markAsRead: async (notificationId: string, token?: string): Promise<ApiResult<void>> => {
    if (USE_MOCK) {
      // モックモード
      sendDebugLog(`markAsRead: モックモード notificationId=${notificationId}`);
      await new Promise(resolve => setTimeout(resolve, 100));
      markMockAsRead(notificationId);
      return { success: true, data: undefined };
    }

    sendDebugLog(`markAsRead: API呼出 notificationId=${notificationId}, tokenあり=${!!token}`);

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const client = createApiClient({ baseUrl: API_BASE_URL, headers });
    const result = await client.put<void>(
      `/api/v1/notifications/${notificationId}/read`
    );

    return result;
  },

  /**
   * 未読件数を取得
   */
  getUnreadCount: async (accountId: string, token?: string): Promise<ApiResult<number>> => {
    if (USE_MOCK) {
      // モックモード
      sendDebugLog(`getUnreadCount: モックモード使用`);
      await new Promise(resolve => setTimeout(resolve, 100));
      const count = getMockUnreadCount();
      sendDebugLog(`getUnreadCount: モック返却 count=${count}`);
      return { success: true, data: count };
    }

    sendDebugLog(`getUnreadCount: API呼出 accountId=${accountId}, tokenあり=${!!token}`);

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const client = createApiClient({ baseUrl: API_BASE_URL, headers });
    const result = await client.get<{ count: number }>(
      `/api/v1/notifications/unread-count?accountId=${accountId}`
    );

    if (!result.success) {
      return result;
    }

    return { success: true, data: result.data.count };
  },
};
